import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import zxcvbn from 'zxcvbn';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get token and other parameters from URL
  const token = searchParams.get('token');
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');
  const pkceCode = searchParams.get('code'); // PKCE flow sends ?code=

  useEffect(() => {
    // Verify token on component mount
    const verifyToken = async () => {
      console.log('Verifying reset token...', { token, accessToken, type, pkceCode: !!pkceCode });

      try {
        // PKCE flow: exchange the ?code= param for a session
        if (pkceCode) {
          console.log('Processing PKCE password reset code...');
          const { error } = await supabase.auth.exchangeCodeForSession(pkceCode);
          if (error) {
            console.error('PKCE code exchange failed:', error);
            throw error;
          }
          // Clear the code from the URL so refresh doesn't re-run the exchange
          window.history.replaceState({}, '', '/auth/reset-password');
          console.log('PKCE exchange successful, showing reset form');
          setLoading(false);
          return;
        }

        // First check if we already have a valid session
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          console.log('Valid session found, user can reset password');
          setLoading(false);
          return;
        }

        // If we have URL parameters, try to establish a session
        if (type === 'recovery' && (accessToken || token)) {
          console.log('Processing recovery token...');
          
          // Try to verify and set session from URL hash
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: window.location.hash.substring(1), // Remove # from hash
            type: 'recovery'
          });

          if (error) {
            console.error('OTP verification failed:', error);
            // Fallback: try setting session directly if we have tokens
            if (accessToken && refreshToken) {
              const { error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
              });
              
              if (sessionError) {
                throw sessionError;
              }
            } else {
              throw error;
            }
          }

          console.log('Token verified successfully');
          setLoading(false);
        } else if (!token && !accessToken) {
          throw new Error('No reset token found in URL');
        } else {
          // Try the exchange method as fallback
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href);
          
          if (exchangeError) {
            console.error('Token exchange error:', exchangeError);
            throw exchangeError;
          }
          
          setLoading(false);
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        setError('Invalid or expired reset token. Please request a new password reset.');
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, accessToken, refreshToken, type, pkceCode]);

  const passwordStrength = password ? zxcvbn(password) : null;
  const isPasswordStrong = passwordStrength?.score >= 3;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isPasswordStrong) {
      setError('Please choose a stronger password');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setError('Session expired. Please request a new password reset.');
        return;
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password
      });

      if (updateError) throw updateError;

      // Show success message and redirect
      setSuccess(true);
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = () => {
    if (!passwordStrength) return 'bg-slate';
    switch (passwordStrength.score) {
      case 0: return 'bg-crimson';
      case 1: return 'bg-gold';
      case 2: return 'bg-gold';
      case 3: return 'bg-emerald';
      case 4: return 'bg-emerald';
      default: return 'bg-slate';
    }
  };

  const getStrengthText = () => {
    if (!passwordStrength) return '';
    switch (passwordStrength.score) {
      case 0: return 'Very Weak';
      case 1: return 'Weak';
      case 2: return 'Fair';
      case 3: return 'Strong';
      case 4: return 'Very Strong';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sand/30 to-midnight/5 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-sand/20 p-8">
          <Loader2 className="h-8 w-8 animate-spin text-electric mx-auto mb-4" />
          <p className="text-slate text-center">Verifying reset token...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand/30 to-midnight/5">
      <nav className="bg-white/80 backdrop-blur-sm sticky top-0 z-50 border-b border-sand/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <a href="https://sermonbuild.com" className="flex items-center">
              <img 
                src="https://pastorjoshblevins.com/wp-content/uploads/2025/03/logo-1.png" 
                alt="Logo" 
                className="h-8 w-auto"
              />
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-sand/20 overflow-hidden">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-midnight mb-2">
              Reset Your Password
            </h2>
            <p className="text-slate mb-6">
              Please enter your new password below.
            </p>

            {error && (
              <div className="mb-6 p-4 bg-crimson/10 text-crimson rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {success && (
              <div className="mb-6 p-4 bg-emerald/10 text-emerald rounded-lg flex items-center">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span className="text-sm">Password reset successful! Redirecting...</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {passwordStrength && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="h-2 flex-1 bg-sand/20 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${getStrengthColor()} transition-all duration-300`}
                          style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${getStrengthColor().replace('bg-', 'text-')}`}>
                        {getStrengthText()}
                      </span>
                    </div>
                    {passwordStrength.feedback.warning && (
                      <p className="text-xs text-crimson mt-1">{passwordStrength.feedback.warning}</p>
                    )}
                    {passwordStrength.feedback.suggestions?.length > 0 && (
                      <ul className="text-xs text-slate mt-1 space-y-1">
                        {passwordStrength.feedback.suggestions.map((suggestion, index) => (
                          <li key={index} className="flex items-start gap-1">
                            <span className="text-emerald">•</span>
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !isPasswordStrong}
                className="w-full py-2 px-4 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}